"use client";

import NextLink from "next/link";
import {
  Flex,
  Link,
  Button,
  Text,
  HStack,
  Icon,
  VStack,
  Box,
  Collapsible,
  useCollapsible,
} from "@chakra-ui/react";
import { useRouter, usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import {
  LuBookOpen,
  LuChevronDown,
  LuChevronRight,
  LuDumbbell,
  LuHash,
  LuLayoutDashboard,
  LuLibraryBig,
  LuNotebookPen,
  LuSettings,
  LuSpeech,
} from "react-icons/lu";
import { TbAlphabetBangla } from "react-icons/tb";
import { useLoading } from "@/app/context/LoadingProvider";

export function NavLinks({
  onNavigate,
  variant = "inline",
}: {
  onNavigate?: () => void;
  variant?: "inline" | "sidebar";
}) {
  
  const { status } = useSession();
  const isLoggedIn = status === "authenticated";
  const router = useRouter();
  const pathname = usePathname();
  const { setPageLoading } = useLoading();
  const practiceCollapsible = useCollapsible({
    defaultOpen: pathname.startsWith("/Practice"),
  });

  const protectedNavLinks = [
    { href: "/Dashboard", label: "Dashboard", icon: LuLayoutDashboard },


      { href: "/Lessons", label: "Lessons", icon: LuBookOpen },
      { href: "/Alphabets", label: "Alphabets", icon: TbAlphabetBangla },
      { href: "/Numbers", label: "Numbers", icon: LuHash },
      {href: "/Settings", label: "Settings", icon: LuSettings }
  ];
  const practiceLinks = [
    { href: "/Reading", label: "Reading", icon: LuLibraryBig },
    { href: "/Practice/Writing", label: "Writing", icon: LuNotebookPen },
    { href: "/Practice/Speaking", label: "Speaking", icon: LuSpeech },
  ];
  const isPracticeGroupActive = pathname.startsWith("/Practice");

  const handleRedirect = (href: string) => {
    if (href.startsWith("#")) {
      const target = document?.querySelector(href);
      if (target) target.scrollIntoView({ behavior: "smooth" });
      return;
    }
    setPageLoading(true);
    router.push(href);
  };

  if (variant === "sidebar") {
    return (
      <VStack align="stretch" gap={1} w="full">
        {isLoggedIn &&
          protectedNavLinks.map((item) => {
            const active =
              pathname === item.href || pathname.startsWith(item.href + "/");

            return (
              <Box key={item.href}>
                <Button
                  asChild
                  variant={active ? "subtle" : "ghost"}
                  colorPalette={active ? "teal" : undefined}
                  justifyContent="flex-start"
                  w="full"
                  size="sm"
                  h="10"
                  rounded="lg"
                  px="3"
                >
                  <NextLink
                    href={item.href}
                    aria-current={active ? "page" : undefined}
                    onClick={(e) => {
                      if (active) {
                        e.preventDefault();
                        onNavigate?.();
                        return;
                      }
                      setPageLoading(true);
                      onNavigate?.();
                    }}
                  >
                    <HStack gap={2} w="full">
                      <Icon as={item.icon} boxSize="5" />
                      <Text as="span" flex="1" textAlign="left">
                        {item.label}
                      </Text>
                    </HStack>
                  </NextLink>
                </Button>

                {item.href === "/Lessons" ? (
                  <Box mt={1}>
                    <Button
                      variant={isPracticeGroupActive ? "subtle" : "ghost"}
                      colorPalette={isPracticeGroupActive ? "teal" : undefined}
                      justifyContent="flex-start"
                      w="full"
                      size="sm"
                      h="10"
                      rounded="lg"
                      px="3"
                      onClick={() =>
                        practiceCollapsible.setOpen(!practiceCollapsible.open)
                      }
                    >
                      <HStack gap={2} w="full">
                        <Icon as={LuDumbbell} boxSize="5" />
                        <Text as="span" flex="1" textAlign="left">
                          Practice
                        </Text>
                        <Icon as={practiceCollapsible.open ? LuChevronDown : LuChevronRight} />
                      </HStack>
                    </Button>

                    <Collapsible.RootProvider value={practiceCollapsible}>
                      <Collapsible.Content>
                        <VStack align="stretch" gap={1} mt={1} pl={3}>
                          {practiceLinks.map((practiceItem) => {
                            const practiceActive =
                              pathname === practiceItem.href ||
                              pathname.startsWith(practiceItem.href + "/");

                            return (
                              <Button
                                key={practiceItem.href}
                                asChild
                                variant={practiceActive ? "subtle" : "ghost"}
                                colorPalette={practiceActive ? "teal" : undefined}
                                justifyContent="flex-start"
                                w="full"
                                size="sm"
                                h="8"
                                rounded="lg"
                                px="3"
                              >
                                <NextLink
                                  href={practiceItem.href}
                                  aria-current={practiceActive ? "page" : undefined}
                                  onClick={(e) => {
                                    if (practiceActive) {
                                      e.preventDefault();
                                      onNavigate?.();
                                      return;
                                    }
                                    setPageLoading(true);
                                    onNavigate?.();
                                  }}
                                >
                                  <HStack gap={2} w="full">
                                    <Icon as={practiceItem.icon} boxSize="4" />
                                    <Text as="span" flex="1" textAlign="left" fontSize="xs">
                                      {practiceItem.label}
                                    </Text>
                                  </HStack>
                                </NextLink>
                              </Button>
                            );
                          })}
                        </VStack>
                      </Collapsible.Content>
                    </Collapsible.RootProvider>
                  </Box>
                ) : null}
              </Box>
            );
          })}
      </VStack>
    );
  }

  return (
    <Flex 
    gap={8}
    align="center" 
    display='flex' 
    flexDirection={{base:'column', md: 'row'}}>
      {isLoggedIn && protectedNavLinks.map((link) => (
         <Link
          color={pathname == link.href ? 'orange' : ''}
          fontWeight={pathname == link.href ? 'bold' : ''}
          key={link.label}          
          href={link.href}
          fontSize={{lg: "lg"  }}        
          textDecoration={'none'}
          onClick={(e) => {
            e.preventDefault();
            onNavigate?.();
            handleRedirect(link.href);
          }}
        >
          {link.label}
        </Link>        
      ))}

      {/* { publicNavLinks.map((link) => (
         <Link
          color={pathname == link.href ? 'orange' : ''}
          fontWeight={pathname == link.href ? 'bold' : ''}
          key={link.label}          
          href={link.href}
          fontSize="md"          
          textDecoration={'none'}
          onClick={(e) => {
            e.preventDefault();
            handleRedirect(link.href);
          }}
        >
          {link.label}
        </Link>        
      ))} */}
    </Flex>
  );
}
